export type ApiKeyIdentity = {
  /**
   * The id of the API key.
   */
  id: string;
  /**
   * The label of the API key.
   */
  label: string;
  /**
   * The key suffix of the API key. Auto-generated from the last 5 characters of the key.
   */
  keySuffix: string;
  /**
   * The key of the API key. This is the actual API key that is used to authenticate requests.
   */
  key: string;
  /**
   * The user name of the API key. If specified, this API key will behave like a "Personal Access Token"
   * and will use the Basic Auth protocol to authenticate requests. If not specified the "Token" protocol will be used
   * with the key as the token.
   */
  userName?: string;

  /**
   * The date the API key was created.
   */
  dateCreated?: string;
  /**
   * The app ID of the API key.
   */
  appId?: string;
};

export type CreateApiKeyInput = Omit<ApiKeyIdentity, "id" | "keySuffix">;

export type EntityIdentity = {
  /**
   * The UUID of the entity.
   */
  id: string;
  /**
   * The label of the entity.
   */
  label: string;
};

type IdentityStorage = {
  primaryEntity?: string;
  entities?: Record<string, EntityIdentity>;
  apiKeys?: Record<string, ApiKeyIdentity>;
};

declare global {
  interface Window {
    apiIdentityService?: ApiIdentityService;
  }
}

/**
 * The ApiIdentityService is a service that provides the API identities for the user.
 */
export class ApiIdentityService {
  constructor(private readonly storage: Storage) {}

  // NOTE: Even though Storage is a synchronous API, we will treat it as asynchronous
  // in case we want to persist to the server later.
  private async getIdentityStorage() {
    // If storage is not provided, use window.localStorage
    const storage = this.storage ?? window?.localStorage;

    const storageData = storage.getItem("apiIdentities");
    if (!storageData) {
      return {};
    }

    // TODO: Validate the storage data
    return JSON.parse(storageData) as IdentityStorage;
  }

  async getEntities(): Promise<EntityIdentity[]> {
    const storageData = await this.getIdentityStorage();
    return Object.values(storageData.entities || {});
  }

  async getApiKeys(): Promise<ApiKeyIdentity[]> {
    const storageData = await this.getIdentityStorage();
    return Object.values(storageData.apiKeys || {});
  }

  async addApiKey(apiKeyInput: CreateApiKeyInput & { id: string }) {
    const storageData = await this.getIdentityStorage();

    // Auto-generate keySuffix from last 5 characters of the key
    const keySuffix =
      apiKeyInput.key.length >= 5
        ? `...${apiKeyInput.key.slice(-5)}`
        : `...${apiKeyInput.key}`;

    const apiKey: ApiKeyIdentity = {
      ...apiKeyInput,
      keySuffix,
    };

    const apiKeys = storageData.apiKeys || {};
    apiKeys[apiKey.id] = apiKey;
    storageData.apiKeys = apiKeys;
    this.storage.setItem("apiIdentities", JSON.stringify(storageData));
  }

  async removeApiKey(apiKeyId: string) {
    const storageData = await this.getIdentityStorage();
    const apiKeys = storageData.apiKeys || {};
    delete apiKeys[apiKeyId];
    storageData.apiKeys = apiKeys;
    this.storage.setItem("apiIdentities", JSON.stringify(storageData));
  }

  async addEntity(entity: EntityIdentity) {
    const storageData = await this.getIdentityStorage();
    const entities = storageData.entities || {};
    entities[entity.id] = entity;
    storageData.entities = entities;
    this.storage.setItem("apiIdentities", JSON.stringify(storageData));
  }

  async removeEntity(entityId: string) {
    const storageData = await this.getIdentityStorage();
    const entities = storageData.entities || {};
    delete entities[entityId];
    storageData.entities = entities;
    this.storage.setItem("apiIdentities", JSON.stringify(storageData));
  }

  private static instance?: ApiIdentityService;

  /**
   * Get the global singleton instance of the ApiIdentityService.
   * @returns The global singleton instance of the ApiIdentityService
   */
  public static getInstance(): ApiIdentityService {
    if (typeof window === "undefined") {
      throw new Error("ApiIdentityService can only be used in the browser.");
    }
    let instance = ApiIdentityService.instance;
    if (!instance) {
      instance = new ApiIdentityService(window?.localStorage);
      ApiIdentityService.instance = instance;
    }
    return instance;
  }
}
